import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Loader2, RefreshCcw, Download, MessageSquare, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { useStaffAuth } from '../../context/StaffAuthContext';
import { apiErrorMessage } from '../../lib/apiError';
import { Button } from '../../components/ui/button';

const STATUSES = ['new', 'in_review', 'escalated', 'resolved', 'closed'];
const STATUS_LABEL = {
  new: 'New', in_review: 'In review', escalated: 'Escalated', resolved: 'Resolved', closed: 'Closed',
};
const STATUS_STYLES = {
  new: 'bg-blue-100 text-blue-800',
  in_review: 'bg-amber-100 text-amber-800',
  escalated: 'bg-rose-100 text-rose-800',
  resolved: 'bg-emerald-100 text-emerald-800',
  closed: 'bg-[#0B3B2E]/10 text-[#0B3B2E]',
};
const URGENCY_STYLES = {
  high: 'text-rose-700',
  medium: 'text-amber-700',
  low: 'text-[#0E1F1A]/60',
};
const PRODUCT_LABEL = {
  vector_settle: 'Vector Settle', vector_bridge: 'Vector Bridge', bulk_pay: 'Bulk Pay',
  vector_forward: 'Vector Forward', global_payroll: 'Global Payroll',
  multi_currency: 'Multi-currency account', personal_transfers: 'Personal transfers', other: 'Other',
};

export default function CrmComplaints() {
  const { staff, authHeader, API } = useStaffAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = statusFilter ? `${API}/crm/complaints?status=${statusFilter}` : `${API}/crm/complaints`;
      const res = await axios.get(url, { headers: authHeader() });
      setItems(res.data.complaints || []);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not load complaints'));
    } finally {
      setLoading(false);
    }
  }, [API, authHeader, statusFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    setNoteDraft(selected?.internal_note || '');
  }, [selected]);

  const counts = useMemo(() => {
    const c = { total: items.length };
    STATUSES.forEach((s) => { c[s] = items.filter((i) => i.status === s).length; });
    return c;
  }, [items]);

  const patch = async (id, body) => {
    setSaving(true);
    try {
      const res = await axios.patch(`${API}/crm/complaints/${id}`, body, { headers: authHeader() });
      setItems((prev) => prev.map((i) => (i.id === id ? res.data : i)));
      if (selected?.id === id) setSelected(res.data);
      toast.success('Complaint updated');
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not update complaint'));
    } finally {
      setSaving(false);
    }
  };

  const downloadAttachment = async (c) => {
    try {
      const res = await axios.get(`${API}/crm/complaints/${c.id}/attachment`, {
        headers: authHeader(), responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = c.attachment?.filename || 'attachment';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Could not download attachment'));
    }
  };

  if (staff.role === 'rm') {
    return (
      <div className="p-10 max-w-[900px] mx-auto">
        <div className="bg-white rounded-2xl border border-[#0B3B2E]/10 p-10 text-center">
          <MessageSquare className="w-8 h-8 mx-auto text-[#0B3B2E]/40" />
          <h1 className="mt-4 font-serif text-[24px] text-[#0B3B2E]">Admin &amp; Manager only</h1>
          <p className="text-[13px] text-[#0E1F1A]/60 mt-2">
            Complaints handling is restricted to compliance-approved roles.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] mx-auto" data-testid="crm-complaints-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#0B3B2E]/70 font-medium">CRM</p>
          <h1 className="mt-2 font-serif text-[32px] lg:text-[40px] leading-tight text-[#0B3B2E]">Complaints</h1>
          <p className="mt-1 text-[13px] text-[#0E1F1A]/60">
            Every complaint submitted from the public site lands here. Escalate high-urgency items to compliance.
          </p>
        </div>
        <Button onClick={load} variant="outline"
          className="h-10 rounded-full border-[#0B3B2E]/25 bg-transparent text-[#0B3B2E] hover:bg-[#0B3B2E]/5"
          data-testid="complaints-refresh">
          <RefreshCcw className="w-4 h-4 mr-1.5" /> Refresh
        </Button>
      </div>

      {/* Status summary */}
      <div className="mt-6 grid grid-cols-2 md:grid-cols-6 gap-3">
        <SummaryPill label="Total" value={counts.total} active={!statusFilter} onClick={() => setStatusFilter('')} />
        {STATUSES.map((s) => (
          <SummaryPill key={s} label={STATUS_LABEL[s]} value={counts[s]} active={statusFilter === s}
            onClick={() => setStatusFilter(s === statusFilter ? '' : s)} testId={`complaints-filter-${s}`} />
        ))}
      </div>

      {/* Table */}
      <div className="mt-6 bg-white rounded-2xl border border-[#0B3B2E]/10 overflow-hidden">
        {loading ? (
          <div className="p-16 flex items-center justify-center text-[#0B3B2E]">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="p-16 text-center text-[#0E1F1A]/55 text-[14px]">No complaints yet.</div>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="bg-[#0B3B2E]/5 text-[#0B3B2E]">
              <tr>
                <Th>Submitter</Th>
                <Th>Subject</Th>
                <Th>Product</Th>
                <Th>Urgency</Th>
                <Th>Status</Th>
                <Th>Received</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr key={c.id} className="border-t border-[#0B3B2E]/8 hover:bg-[#F5F1E8]/40">
                  <Td>
                    <p className="font-medium text-[#0B3B2E]">{c.name}</p>
                    <p className="text-[11px] text-[#0E1F1A]/55">{c.email}</p>
                  </Td>
                  <Td>
                    <p className="font-medium text-[#0B3B2E] line-clamp-1 max-w-[280px]">{c.subject}</p>
                    <p className="text-[11px] text-[#0E1F1A]/50 line-clamp-1 max-w-[280px]">{c.description}</p>
                  </Td>
                  <Td className="text-[#0E1F1A]/80">{PRODUCT_LABEL[c.product] || '—'}</Td>
                  <Td>
                    <span className={`text-[12px] font-medium uppercase tracking-wide ${URGENCY_STYLES[c.urgency] || ''}`}>
                      {c.urgency}
                    </span>
                  </Td>
                  <Td>
                    <select value={c.status}
                      data-testid={`complaint-status-${c.id}`}
                      onChange={(e) => patch(c.id, { status: e.target.value })}
                      className={`h-8 rounded-full px-2.5 text-[11px] font-medium border-0 outline-none ${STATUS_STYLES[c.status] || ''}`}>
                      {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                    </select>
                  </Td>
                  <Td className="whitespace-nowrap text-[#0E1F1A]/55">
                    {c.created_at ? new Date(c.created_at).toLocaleDateString('en-GB') : '—'}
                  </Td>
                  <Td>
                    <button
                      onClick={() => setSelected(c)}
                      data-testid={`complaint-open-${c.id}`}
                      className="inline-flex items-center gap-1 text-[12px] text-[#0B3B2E] font-medium hover:text-[#12503F]"
                    >
                      Open <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-40 flex" data-testid="complaint-drawer">
          <button className="flex-1 bg-black/30" aria-label="Close drawer" onClick={() => setSelected(null)} />
          <aside className="w-full max-w-[560px] bg-[#F5F1E8] h-full overflow-y-auto border-l border-[#0B3B2E]/15">
            <div className="p-6 border-b border-[#0B3B2E]/10 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#0B3B2E]/60">
                  Complaint · {STATUS_LABEL[selected.status] || selected.status}
                </p>
                <h2 className="font-serif text-[22px] text-[#0B3B2E] leading-tight mt-1 break-words">{selected.subject}</h2>
              </div>
              <button onClick={() => setSelected(null)} className="text-[13px] text-[#0B3B2E] font-medium">Close</button>
            </div>
            <div className="p-6 space-y-5 text-[13px]">
              <DetailRow label="From">{selected.name} &middot; <a className="underline" href={`mailto:${selected.email}`}>{selected.email}</a></DetailRow>
              <DetailRow label="Product">{PRODUCT_LABEL[selected.product] || 'Other'}</DetailRow>
              <DetailRow label="Preferred contact">{selected.contact_method}</DetailRow>
              <DetailRow label="Urgency"><span className={`font-medium uppercase ${URGENCY_STYLES[selected.urgency] || ''}`}>{selected.urgency}</span></DetailRow>
              {selected.reference_id && <DetailRow label="Reference">{selected.reference_id}</DetailRow>}
              <DetailRow label="Received">{selected.created_at ? new Date(selected.created_at).toLocaleString('en-GB') : '—'}</DetailRow>

              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#0B3B2E]/55 mb-1.5">Description</p>
                <p className="whitespace-pre-wrap leading-relaxed text-[#0E1F1A]/85">{selected.description}</p>
              </div>

              {selected.attachment && (
                <div>
                  <p className="text-[11px] uppercase tracking-[0.2em] text-[#0B3B2E]/55 mb-1.5">Attachment</p>
                  <button onClick={() => downloadAttachment(selected)}
                    className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-[#0B3B2E] text-[#F5F1E8] text-[12px] font-medium hover:bg-[#12503F]"
                    data-testid="complaint-attachment-download">
                    <Download className="w-3.5 h-3.5" /> {selected.attachment.filename}
                  </button>
                </div>
              )}

              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#0B3B2E]/55 mb-1.5">Status</p>
                <select value={selected.status}
                  onChange={(e) => patch(selected.id, { status: e.target.value })}
                  data-testid="complaint-drawer-status"
                  className={`h-9 rounded-full px-3 text-[12px] font-medium border-0 outline-none ${STATUS_STYLES[selected.status] || ''}`}>
                  {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABEL[s]}</option>)}
                </select>
              </div>

              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#0B3B2E]/55 mb-1.5">Internal note</p>
                <textarea value={noteDraft} onChange={(e) => setNoteDraft(e.target.value)}
                  data-testid="complaint-note-input"
                  rows={4}
                  placeholder="Log actions taken, calls made, or escalation path."
                  className="w-full rounded-md border border-[#0B3B2E]/20 bg-white p-2.5 text-[13px]" />
                <Button
                  onClick={() => patch(selected.id, { internal_note: noteDraft })}
                  disabled={saving || noteDraft === (selected.internal_note || '')}
                  data-testid="complaint-note-save"
                  className="mt-2 rounded-full h-9 bg-[#0B3B2E] text-[#F5F1E8] hover:bg-[#12503F] disabled:opacity-60">
                  {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save note'}
                </Button>
              </div>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left px-4 py-3 text-[11px] uppercase tracking-wider font-medium">{children}</th>;
}

function Td({ children, className = '' }) {
  return <td className={`px-4 py-3 align-top ${className}`}>{children}</td>;
}

function SummaryPill({ label, value, active, onClick, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`rounded-2xl p-4 text-left border transition-all ${
        active
          ? 'bg-[#0B3B2E] text-[#F5F1E8] border-[#0B3B2E]'
          : 'bg-white border-[#0B3B2E]/12 text-[#0B3B2E] hover:border-[#0B3B2E]/25'
      }`}>
      <p className="text-[10px] uppercase tracking-[0.18em] opacity-70">{label}</p>
      <p className="mt-1 font-serif text-[26px] leading-none">{value}</p>
    </button>
  );
}

function DetailRow({ label, children }) {
  return (
    <div className="flex items-baseline gap-3">
      <p className="text-[11px] uppercase tracking-[0.18em] text-[#0B3B2E]/55 w-[110px] shrink-0">{label}</p>
      <div className="text-[#0E1F1A]/85 break-words min-w-0">{children}</div>
    </div>
  );
}
